import { Schema } from "effect";
import {
  AccountId,
  ChannelThreadId,
  ConversationTimelineEventId,
  MessageId,
  ShopperAiTagId,
  SupportTicketId,
  TopicId,
} from "../../../../ids/definitions";
import {
  MongoCustomerId,
  MongoRuleId,
  MongoUserId,
} from "../../../../ids/mongo-ids";
import { DateFromDb } from "../../../../schema/helpers";
import { MessageChannel } from "../message";
import { SupportTicketPriority, SupportTicketStatus } from "../support-ticket";

export const TimelineActorType = Schema.Literal(
  "SYSTEM",
  "RULE",
  "USER",
  "CUSTOMER",
  "AI",
  "EXTERNAL_USER",
);
export type TimelineActorType = typeof TimelineActorType.Type;

// Event type literals
export const TIMELINE_EVENT_TYPES = [
  "support_ticket_created",
  "support_ticket_status_changed",
  "support_ticket_priority_changed",
  "support_ticket_assigned",
  "support_ticket_read",
  "support_ticket_merged",
  "support_ticket_merge_undone",
  "topic_handoff",
  "message_sent",
  "message_received",
  "thread_created",
  "tag_added",
  "tag_removed",
  "rules_triggered",
  "conversation_insights_generated",
  "comment_hidden",
  "comment_unhidden",
  "comment_deleted",
  "comment_liked",
  "comment_un_liked",
  "support_ticket_migrated_from_v1",
  "legacy_timeline_event",
] as const;

export const TimelineEventType = Schema.Literal(...TIMELINE_EVENT_TYPES);
export type TimelineEventType = typeof TimelineEventType.Type;

// Operational metadata captured when an event is written inside a Temporal
// activity. Used by internal tooling to deep-link into the workflow execution.
// `namespace` is optional for backwards compatibility with events recorded
// before it was captured.
export const TimelineEventMetadata = Schema.Struct({
  workflowId: Schema.String,
  activityId: Schema.String,
  namespace: Schema.optional(Schema.String),
});
export type TimelineEventMetadata = typeof TimelineEventMetadata.Type;

// Base fields shared by all timeline events
const BaseTimelineEventFields = {
  id: ConversationTimelineEventId,
  topicId: TopicId,
  accountId: AccountId,
  actorType: TimelineActorType,
  actorMongoUserId: Schema.NullOr(MongoUserId),
  actorMongoCustomerId: Schema.NullOr(MongoCustomerId),
  actorMongoRuleId: Schema.NullOr(MongoRuleId),
  parentEventId: Schema.NullOr(ConversationTimelineEventId),
  metadata: Schema.optionalWith(TimelineEventMetadata, { nullable: true }),
  occurredAt: DateFromDb,
};

// Event data schemas for each event type
export const SupportTicketCreatedEventData = Schema.Struct({
  status: SupportTicketStatus,
  initialMessageId: Schema.optional(MessageId),
  threadId: Schema.optional(ChannelThreadId),
  channel: Schema.optional(MessageChannel),
});
export type SupportTicketCreatedEventData =
  typeof SupportTicketCreatedEventData.Type;

export const SupportTicketStatusChangedEventData = Schema.Struct({
  previousStatus: SupportTicketStatus,
  newStatus: SupportTicketStatus,
  reason: Schema.optional(Schema.String),
});
export type SupportTicketStatusChangedEventData =
  typeof SupportTicketStatusChangedEventData.Type;

export const SupportTicketPriorityChangedEventData = Schema.Struct({
  previousPriority: Schema.NullOr(SupportTicketPriority),
  newPriority: Schema.NullOr(SupportTicketPriority),
});
export type SupportTicketPriorityChangedEventData =
  typeof SupportTicketPriorityChangedEventData.Type;

export const SupportTicketAssignedEventData = Schema.Struct({
  assignedToMongoUserId: Schema.optional(MongoUserId),
  previousAssigneeMongoUserId: Schema.optional(MongoUserId),
});
export type SupportTicketAssignedEventData =
  typeof SupportTicketAssignedEventData.Type;

export const SupportTicketReadEventData = Schema.Struct({
  readByMongoUserId: MongoUserId,
});
export type SupportTicketReadEventData = typeof SupportTicketReadEventData.Type;

export const SupportTicketMergeEventData = Schema.Struct({
  // Older merge timeline events were written before relationship ids were
  // included, so undo callers fall back to source/destination ids for those.
  relationshipId: Schema.optional(Schema.String),
  sourceSupportTicketId: SupportTicketId,
  destinationSupportTicketId: SupportTicketId,
  sourceTicketNumber: Schema.NullOr(Schema.Number),
  destinationTicketNumber: Schema.NullOr(Schema.Number),
});
export type SupportTicketMergeEventData =
  typeof SupportTicketMergeEventData.Type;

export const TopicHandoffEndpoint = Schema.Union(
  Schema.Struct({
    _tag: Schema.Literal("topic"),
    topicId: TopicId,
    label: Schema.optional(Schema.String),
  }),
  Schema.Struct({
    _tag: Schema.Literal("actor"),
    actorType: Schema.Literal(
      "ai",
      "chat_flow",
      "human_team",
      "system",
      "external",
    ),
    label: Schema.optional(Schema.String),
  }),
);
export type TopicHandoffEndpoint = typeof TopicHandoffEndpoint.Type;

export const TopicHandoffEventData = Schema.Struct({
  from: TopicHandoffEndpoint,
  to: TopicHandoffEndpoint,
  threadId: Schema.optional(ChannelThreadId),
  channel: Schema.optional(MessageChannel),
  reason: Schema.optional(Schema.String),
});
export type TopicHandoffEventData = typeof TopicHandoffEventData.Type;

export const MessageSentEventData = Schema.Struct({
  messageId: MessageId,
  threadId: ChannelThreadId,
  channel: MessageChannel,
  originTopicId: Schema.optional(TopicId),
  preview: Schema.optional(Schema.String),
});
export type MessageSentEventData = typeof MessageSentEventData.Type;

export const MessageReceivedEventData = Schema.Struct({
  messageId: MessageId,
  threadId: ChannelThreadId,
  channel: MessageChannel,
  originTopicId: Schema.optional(TopicId),
  preview: Schema.optional(Schema.String),
});
export type MessageReceivedEventData = typeof MessageReceivedEventData.Type;

export const ThreadCreatedEventData = Schema.Struct({
  threadId: ChannelThreadId,
  channel: MessageChannel,
  externalThreadId: Schema.String,
  initialMessageId: Schema.optional(MessageId),
});
export type ThreadCreatedEventData = typeof ThreadCreatedEventData.Type;

/** Why the system added a tag, when no person or rule chose it. */
export const TagAddedReason = Schema.TaggedStruct("provider_spam", {
  channel: MessageChannel,
});
export type TagAddedReason = typeof TagAddedReason.Type;

export const TagAddedEventData = Schema.Struct({
  tagId: ShopperAiTagId,
  tagName: Schema.String,
  autoApplied: Schema.optional(Schema.Boolean),
  reason: Schema.optional(TagAddedReason),
});
export type TagAddedEventData = typeof TagAddedEventData.Type;

export const TagRemovedEventData = Schema.Struct({
  tagId: ShopperAiTagId,
  tagName: Schema.String,
});
export type TagRemovedEventData = typeof TagRemovedEventData.Type;

export const CommentActionEventData = Schema.Struct({
  messageId: MessageId,
  commentId: Schema.String,
  platform: Schema.Literal("facebook", "instagram"),
});
export type CommentActionEventData = typeof CommentActionEventData.Type;

export const PrefilteredRuleInfo = Schema.Struct({
  ruleId: MongoRuleId,
  ruleName: Schema.String,
});
export type PrefilteredRuleInfo = typeof PrefilteredRuleInfo.Type;

export const TriggeredRuleInfo = Schema.Struct({
  ruleId: MongoRuleId,
  workflowId: Schema.String,
  ruleName: Schema.String,
  // Whether the rule's conditions passed. Present for inline rules; omitted for
  // advanced-flow rules, whose conditions are evaluated asynchronously and are
  // unknown when the rule is recorded as triggered.
  conditionsPassed: Schema.optional(Schema.Boolean),
});
export type TriggeredRuleInfo = typeof TriggeredRuleInfo.Type;

export const RulesTriggeredEventData = Schema.Struct({
  triggerType: Schema.String, // RuleTriggerType from redo-model, kept as string to avoid circular import
  rules: Schema.Array(TriggeredRuleInfo),
  prefilteredRules: Schema.optional(Schema.Array(PrefilteredRuleInfo)),
});
export type RulesTriggeredEventData = typeof RulesTriggeredEventData.Type;

export const ConversationInsightsGeneratedEventData = Schema.Struct({
  // Sentiment captured at insight-generation time, surfaced so the timeline and
  // rule trigger metadata can show what was produced. Null when the model did
  // not return a sentiment. Kept as a plain string (the authoritative,
  // constrained value lives in support_ticket_insights.customer_sentiment).
  customerSentiment: Schema.NullOr(Schema.String),
});
export type ConversationInsightsGeneratedEventData =
  typeof ConversationInsightsGeneratedEventData.Type;

export const SupportTicketMigratedFromV1EventData = Schema.Struct({
  legacyConversationId: Schema.String,
  migrationVersion: Schema.String,
  threadsMigrated: Schema.Number,
  messagesMigrated: Schema.Number,
  legacyTimelineEventsMigrated: Schema.Number,
});
export type SupportTicketMigratedFromV1EventData =
  typeof SupportTicketMigratedFromV1EventData.Type;

export const LegacyTimelineEventData = Schema.Struct({
  legacyTimelineEventId: Schema.String,
  legacyConversationId: Schema.String,
  legacyEventType: Schema.String,
  shortDescription: Schema.String,
  longDescription: Schema.optional(Schema.String),
  message: Schema.optional(Schema.String),
});
export type LegacyTimelineEventData = typeof LegacyTimelineEventData.Type;

export const AnyTimelineEventData = Schema.Union(
  SupportTicketCreatedEventData,
  SupportTicketStatusChangedEventData,
  SupportTicketPriorityChangedEventData,
  SupportTicketAssignedEventData,
  SupportTicketReadEventData,
  SupportTicketMergeEventData,
  TopicHandoffEventData,
  MessageSentEventData,
  MessageReceivedEventData,
  ThreadCreatedEventData,
  TagAddedEventData,
  TagRemovedEventData,
  RulesTriggeredEventData,
  ConversationInsightsGeneratedEventData,
  CommentActionEventData,
  SupportTicketMigratedFromV1EventData,
  LegacyTimelineEventData,
);
export type AnyTimelineEventData = typeof AnyTimelineEventData.Type;

// Individual event schemas with discriminator
const SupportTicketCreatedEvent = Schema.Struct({
  ...BaseTimelineEventFields,
  eventType: Schema.Literal("support_ticket_created"),
  eventData: SupportTicketCreatedEventData,
});

const SupportTicketStatusChangedEvent = Schema.Struct({
  ...BaseTimelineEventFields,
  eventType: Schema.Literal("support_ticket_status_changed"),
  eventData: SupportTicketStatusChangedEventData,
});

const SupportTicketPriorityChangedEvent = Schema.Struct({
  ...BaseTimelineEventFields,
  eventType: Schema.Literal("support_ticket_priority_changed"),
  eventData: SupportTicketPriorityChangedEventData,
});

const SupportTicketAssignedEvent = Schema.Struct({
  ...BaseTimelineEventFields,
  eventType: Schema.Literal("support_ticket_assigned"),
  eventData: SupportTicketAssignedEventData,
});

const SupportTicketReadEvent = Schema.Struct({
  ...BaseTimelineEventFields,
  eventType: Schema.Literal("support_ticket_read"),
  eventData: SupportTicketReadEventData,
});

const SupportTicketMergedEvent = Schema.Struct({
  ...BaseTimelineEventFields,
  eventType: Schema.Literal("support_ticket_merged"),
  eventData: SupportTicketMergeEventData,
});

const SupportTicketMergeUndoneEvent = Schema.Struct({
  ...BaseTimelineEventFields,
  eventType: Schema.Literal("support_ticket_merge_undone"),
  eventData: SupportTicketMergeEventData,
});

const TopicHandoffEvent = Schema.Struct({
  ...BaseTimelineEventFields,
  eventType: Schema.Literal("topic_handoff"),
  eventData: TopicHandoffEventData,
});

const MessageSentEvent = Schema.Struct({
  ...BaseTimelineEventFields,
  eventType: Schema.Literal("message_sent"),
  eventData: MessageSentEventData,
});

const MessageReceivedEvent = Schema.Struct({
  ...BaseTimelineEventFields,
  eventType: Schema.Literal("message_received"),
  eventData: MessageReceivedEventData,
});

const ThreadCreatedEvent = Schema.Struct({
  ...BaseTimelineEventFields,
  eventType: Schema.Literal("thread_created"),
  eventData: ThreadCreatedEventData,
});

const TagAddedEvent = Schema.Struct({
  ...BaseTimelineEventFields,
  eventType: Schema.Literal("tag_added"),
  eventData: TagAddedEventData,
});

const TagRemovedEvent = Schema.Struct({
  ...BaseTimelineEventFields,
  eventType: Schema.Literal("tag_removed"),
  eventData: TagRemovedEventData,
});

const RulesTriggeredEvent = Schema.Struct({
  ...BaseTimelineEventFields,
  eventType: Schema.Literal("rules_triggered"),
  eventData: RulesTriggeredEventData,
});

const ConversationInsightsGeneratedEvent = Schema.Struct({
  ...BaseTimelineEventFields,
  eventType: Schema.Literal("conversation_insights_generated"),
  eventData: ConversationInsightsGeneratedEventData,
});

const CommentHiddenEvent = Schema.Struct({
  ...BaseTimelineEventFields,
  eventType: Schema.Literal("comment_hidden"),
  eventData: CommentActionEventData,
});

const CommentUnhiddenEvent = Schema.Struct({
  ...BaseTimelineEventFields,
  eventType: Schema.Literal("comment_unhidden"),
  eventData: CommentActionEventData,
});

const CommentDeletedEvent = Schema.Struct({
  ...BaseTimelineEventFields,
  eventType: Schema.Literal("comment_deleted"),
  eventData: CommentActionEventData,
});

const CommentLikedEvent = Schema.Struct({
  ...BaseTimelineEventFields,
  eventType: Schema.Literal("comment_liked"),
  eventData: CommentActionEventData,
});

const CommentUnlikedEvent = Schema.Struct({
  ...BaseTimelineEventFields,
  eventType: Schema.Literal("comment_un_liked"),
  eventData: CommentActionEventData,
});

const SupportTicketMigratedFromV1Event = Schema.Struct({
  ...BaseTimelineEventFields,
  eventType: Schema.Literal("support_ticket_migrated_from_v1"),
  eventData: SupportTicketMigratedFromV1EventData,
});

const LegacyTimelineEvent = Schema.Struct({
  ...BaseTimelineEventFields,
  eventType: Schema.Literal("legacy_timeline_event"),
  eventData: LegacyTimelineEventData,
});

// Discriminated union on eventType
export const ConversationTimelineEvent = Schema.Union(
  SupportTicketCreatedEvent,
  SupportTicketStatusChangedEvent,
  SupportTicketPriorityChangedEvent,
  SupportTicketAssignedEvent,
  SupportTicketReadEvent,
  SupportTicketMergedEvent,
  SupportTicketMergeUndoneEvent,
  TopicHandoffEvent,
  MessageSentEvent,
  MessageReceivedEvent,
  ThreadCreatedEvent,
  TagAddedEvent,
  TagRemovedEvent,
  RulesTriggeredEvent,
  ConversationInsightsGeneratedEvent,
  CommentHiddenEvent,
  CommentUnhiddenEvent,
  CommentDeletedEvent,
  CommentLikedEvent,
  CommentUnlikedEvent,
  SupportTicketMigratedFromV1Event,
  LegacyTimelineEvent,
);
export type ConversationTimelineEvent = typeof ConversationTimelineEvent.Type;
